/**
 * Per-event PWA branding.
 *
 * public/manifest.webmanifest is the static baseline (name "E2M OnAir", square
 * icons derived from the existing e2m.live logo) and is what makes the app
 * installable at all. Once a user is inside an event, this module rewrites the
 * manifest from that event's own branding — its name, header colour and logo — so
 * an install performed from inside the event lands on the home screen with the
 * event's identity rather than the generic one.
 *
 * It is purely additive: it renders nothing, changes no app behaviour, and every
 * step degrades to the static manifest if anything is unavailable.
 *
 *   - The event logo is wide (typically 960x192); a manifest icon must be square,
 *     so it is letterboxed onto a square canvas in the browser.
 *   - That requires a CORS-readable image. If the CDN does not allow it, the
 *     canvas is tainted, export throws, and we keep the baseline icons.
 *   - The rewritten manifest is served from a blob: URL. Chrome supports this;
 *     Safari does not, and simply keeps the static manifest.
 */
import store from './store'

const STATIC_MANIFEST = '/manifest.webmanifest'
const DEFAULT_THEME = '#f6682f'
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

let currentBlobUrl: string | null = null
let lastSignature = ''

function manifestLink(): HTMLLinkElement | null {
	return document.querySelector('link[rel="manifest"]')
}

function brandingColor(): string {
	const branding = (store.getters.getEventBranding || [])[0] || {}
	const color = branding.topBar || branding.headerBar
	return typeof color === 'string' && HEX.test(color.trim()) ? color.trim() : DEFAULT_THEME
}

function eventLogo(): string {
	const branding = (store.getters.getEventBranding || [])[0] || {}
	return typeof branding.eventLogo === 'string' ? branding.eventLogo : ''
}

/**
 * Letterboxes a remote logo onto an opaque square canvas.
 * Resolves to a data URL, or null when the image can't be read cross-origin.
 */
function squareIconFromUrl(url: string, size: number, background: string): Promise<string | null> {
	return new Promise((resolve) => {
		const image = new Image()
		image.crossOrigin = 'anonymous'
		image.referrerPolicy = 'no-referrer'

		const fail = () => resolve(null)
		// Don't let a slow CDN hold the manifest update open forever.
		const timer = window.setTimeout(fail, 8000)

		image.onerror = () => {
			window.clearTimeout(timer)
			fail()
		}
		image.onload = () => {
			window.clearTimeout(timer)
			try {
				const canvas = document.createElement('canvas')
				canvas.width = size
				canvas.height = size
				const ctx = canvas.getContext('2d')
				if (!ctx) return fail()

				ctx.fillStyle = background
				ctx.fillRect(0, 0, size, size)

				const box = size * 0.84 // leave a margin so maskable cropping is safe
				const scale = Math.min(box / image.width, box / image.height)
				const w = image.width * scale
				const h = image.height * scale
				ctx.drawImage(image, (size - w) / 2, (size - h) / 2, w, h)

				// Throws a SecurityError if the CDN sent no CORS headers.
				resolve(canvas.toDataURL('image/png'))
			} catch (e) {
				console.warn('[pwa] event logo is not CORS-readable; keeping default icons')
				fail()
			}
		}

		image.src = url
	})
}

async function applyEventManifest(): Promise<void> {
	const link = manifestLink()
	if (!link) return

	const name: string = store.getters.getEventName || ''
	const logo = eventLogo()
	const theme = brandingColor()

	// Nothing event-specific to say yet — leave the static manifest in place.
	if (!name && !logo) return

	const signature = [name, logo, theme].join('|')
	if (signature === lastSignature) return
	lastSignature = signature

	const icons: Array<Record<string, string>> = []
	if (logo) {
		const [small, large] = await Promise.all([
			squareIconFromUrl(logo, 192, '#ffffff'),
			squareIconFromUrl(logo, 512, '#ffffff'),
		])
		if (small && large) {
			icons.push(
				{ src: small, sizes: '192x192', type: 'image/png', purpose: 'any' },
				{ src: large, sizes: '512x512', type: 'image/png', purpose: 'any' },
				{ src: large, sizes: '512x512', type: 'image/png', purpose: 'maskable' }
			)
		}
	}

	// Fall back to the baseline square e2m icons — absolute URLs, because a blob:
	// manifest has no base to resolve relative paths against.
	if (!icons.length) {
		const origin = window.location.origin
		icons.push(
			{ src: origin + '/app-icons/icon-192.png', sizes: '192x192', type: 'image/png', purpose: 'any' },
			{ src: origin + '/app-icons/icon-512.png', sizes: '512x512', type: 'image/png', purpose: 'any' },
			{ src: origin + '/app-icons/icon-maskable-512.png', sizes: '512x512', type: 'image/png', purpose: 'maskable' }
		)
	}

	const manifest = {
		name: name || 'E2M OnAir',
		short_name: (name || 'E2M OnAir').slice(0, 30),
		description: name ? name + ' — powered by E2M OnAir' : 'E2M OnAir',
		id: '/app/',
		// Matches public/manifest.webmanifest: launch into the mobile UI, but keep
		// the whole origin in scope so classic pages open in the app window too.
		start_url: window.location.origin + '/app/home',
		scope: window.location.origin + '/',
		display: 'standalone',
		display_override: ['standalone', 'minimal-ui', 'browser'],
		orientation: 'portrait',
		background_color: '#ffffff',
		theme_color: theme,
		lang: 'en',
		icons,
	}

	try {
		const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' })
		const url = URL.createObjectURL(blob)
		link.setAttribute('href', url)
		if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl)
		currentBlobUrl = url
	} catch (e) {
		console.warn('[pwa] could not apply event manifest; keeping the default', e)
		link.setAttribute('href', STATIC_MANIFEST)
	}
}

function schedule(): void {
	applyEventManifest().catch((e) => console.warn('[pwa] manifest update failed', e))
}

// Run once for a restored session, then whenever the active event changes.
if (typeof window !== 'undefined') {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', schedule, { once: true })
	} else {
		schedule()
	}

	store.watch(
		(state: any) => [state.eventname, (state.EventBranding || [])[0]?.eventLogo].join('|'),
		schedule
	)
}
